import React, { ReactNode, useEffect, useState } from "react";
import s from "../Segments.module.scss";
import cn from "classnames";
import BlockBackground from "../../Common/BlockBackground";
import type { RichTextContent } from "../../../lib/storyblok/types/fieldtypes/richText";
import { useContentStyle } from "../../../hooks/useStyle";
import { StyleVariant } from "../../../lib/storyblok/types/shared";
import { motion, useAnimation } from "framer-motion";
import { useSegmentsContext } from "./SegmentsContext";
import useMediaQuery from "../../../hooks/useMatchMedia";
import IconWithColor from "../../Common/IconWithColor";
import BlockIntro from "../../Common/BlockIntro";

type ListItem = {
  _uid: string;
  icon: string;
  text: string;
};

type Props = {
  _uid: string;
  label: string;
  title: string;
  description: RichTextContent;
  style: StyleVariant;
  buttons?: ReactNode;
  image?: ReactNode;
  list?: ListItem[];
  opacity: number;
};

const Item: React.FC<Props> = ({
  _uid,
  label,
  title,
  description,
  buttons,
  style,
  image,
  list,
  opacity = 80,
}) => {
  const { activeItemId, setActiveItemId } = useSegmentsContext();
  const isActive = activeItemId === _uid;
  const isInverted = useContentStyle(style);
  const [isListVisible, setIsListVisible] = useState(false);
  const itemAnimation = useAnimation();
  const isDesktop = useMediaQuery("(min-width: 1025px)");

  const backgroundDefaultOpacity = image ? opacity : 100;
  const backgroundActiveOpacity = image ? 90 : 100;

  useEffect(() => {
    const animateItem = async () => {
      if (isDesktop) {
        await itemAnimation.start({
          flex: isActive ? 2.5 : 1,
          transition: {
            duration: 0.4,
            ease: [0.2, 0.1, 0.2, 1],
          },
        });
      }
    };

    animateItem();

    if (isActive) {
      const timer = setTimeout(() => {
        setIsListVisible(true);
      }, 250);
      return () => clearTimeout(timer);
    } else {
      setIsListVisible(false);
    }
  }, [isActive, itemAnimation, isDesktop]);

  const renderListItems = () => {
    if (!list || list.length === 0) return null;

    if (isDesktop) {
      if (!isActive) return null;

      return (
        <motion.ul
          className={s.itemList}
          initial={{ opacity: 0 }}
          animate={{
            opacity: isListVisible ? 1 : 0,
            y: isListVisible ? 0 : 8,
          }}
          transition={{
            duration: 0.3,
            ease: [0.2, 0.1, 0.2, 1],
          }}
        >
          {list.map((item) => (
            <li key={item._uid} className={s.listItem}>
              <div className={s.iconWrapper}>
                <IconWithColor
                  icon={item.icon}
                  width={14}
                  height={14}
                  alt={""}
                  color={isInverted ? "white" : "black"}
                />
              </div>
              <span className={s.text}>{item.text}</span>
            </li>
          ))}
        </motion.ul>
      );
    } else {
      return (
        <ul className={s.itemList}>
          {list.map((item) => (
            <li key={item._uid} className={s.listItem}>
              <div className={s.iconWrapper}>
                <IconWithColor
                  icon={item.icon}
                  width={14}
                  height={14}
                  alt={item.text}
                  color={isInverted ? "white" : "black"}
                />
              </div>
              <span className={s.text}>{item.text}</span>
            </li>
          ))}
        </ul>
      );
    }
  };

  return (
    <motion.div
      className={cn(s.item, { [s.inverted]: isInverted, [s.active]: isActive })}
      onMouseEnter={() => isDesktop && setActiveItemId(_uid)}
      onMouseLeave={() => isDesktop && setActiveItemId(null)}
      animate={itemAnimation}
      initial={{ flex: 1 }}
      transition={{ duration: 0.3 }}
      layout
    >
      {image && (
        <div className={cn(s.background)}>
          <div className={s.imageContainer}>{image}</div>
        </div>
      )}

      <BlockBackground
        style={style}
        opacity={isActive ? backgroundActiveOpacity : backgroundDefaultOpacity}
      />

      <motion.div className={s.inner}>
        <div className={s.top}>
          <BlockIntro
            label={label}
            title={title}
            inverted={isInverted}
            alignment="alignLeft"
            description={description}
            descriptionSize="small"
            width="full"
          />
        </div>

        <div className={s.bottom}>
          {renderListItems()}
          {buttons && <div className={s.buttons}>{buttons}</div>}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Item;
